import { Product } from '../types/inventory';

export const DEFAULT_PACKAGING_UNITS = [
  'g',
  'kg',
  'L',
  'ml',
  'pièce',
  'carton',
  'Plaquette',
  'boîte(s)',
  'comprimé(s)',
] as const;

export const PACKAGING_UNIT_MAX_LENGTH = 20;

/** Normalise et déduplique une liste d'unités de conditionnement. */
export function normalizePackagingUnits(units: unknown): string[] {
  if (!Array.isArray(units)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of units) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim().slice(0, PACKAGING_UNIT_MAX_LENGTH);
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

/**
 * Liste effective d’unités.
 * Sans fallback : la source de vérité est Firestore (`configuration.products.packagingUnits`).
 * Le seed initial (nouveaux businesses / liste vide) écrit DEFAULT_PACKAGING_UNITS en BD.
 */
export function getPackagingUnitsFromList(
  configuredUnits: string[],
  fallback: readonly string[] = []
): string[] {
  const normalized = normalizePackagingUnits(configuredUnits);
  return normalized.length > 0 ? normalized : [...fallback];
}

/**
 * Complète une liste business avec les unités standard manquantes (sans retirer les custom).
 * Sert à migrer les businesses qui avaient l’ancienne liste (ou une liste partielle).
 */
export function mergeMissingDefaultPackagingUnits(
  configuredUnits: string[],
  defaults: readonly string[] = DEFAULT_PACKAGING_UNITS
): string[] {
  const current = normalizePackagingUnits(configuredUnits);
  const missing = defaults.filter(
    (unit) => !current.some((c) => c.toLowerCase() === unit.toLowerCase())
  );
  return [...current, ...missing];
}

export function needsDefaultPackagingUnitsSync(configuredUnits: string[]): boolean {
  const current = normalizePackagingUnits(configuredUnits);
  const merged = mergeMissingDefaultPackagingUnits(current);
  return merged.length > current.length;
}

export type ProductPackagingInput = {
  packagingValue?: number | null;
  packagingUnit?: string | null;
};

export type ProductPackagingFields = Pick<Product, 'packagingValue' | 'packagingUnit'>;

export type PackagingValidationResult =
  | { valid: true; fields: ProductPackagingFields }
  | { valid: false; error: string };

/**
 * Valide et extrait les champs conditionnement pour persistance produit.
 * Règle : valeur et unité sont toutes les deux renseignées ou toutes les deux absentes.
 */
export function resolveProductPackagingFields(
  input: ProductPackagingInput,
  allowedUnits: string[]
): PackagingValidationResult {
  const hasValue =
    input.packagingValue !== undefined &&
    input.packagingValue !== null &&
    input.packagingValue !== ('' as unknown);
  const hasUnit = Boolean(input.packagingUnit && String(input.packagingUnit).trim());

  if (!hasValue && !hasUnit) {
    return { valid: true, fields: {} };
  }

  if (hasValue && !hasUnit) {
    return { valid: false, error: "L'unité de conditionnement est requise lorsque la valeur est renseignée." };
  }

  if (!hasValue && hasUnit) {
    return { valid: false, error: 'La valeur de conditionnement est requise lorsque l\'unité est renseignée.' };
  }

  const value = Number(input.packagingValue);
  if (!Number.isFinite(value) || value <= 0) {
    return { valid: false, error: 'La valeur de conditionnement doit être un nombre strictement positif.' };
  }

  const unit = String(input.packagingUnit).trim();
  const allowed = getPackagingUnitsFromList(allowedUnits);
  const match = allowed.find((u) => u.toLowerCase() === unit.toLowerCase());
  if (!match) {
    return {
      valid: false,
      error: `Unité « ${unit} » invalide. Unités autorisées : ${allowed.join(', ')}.`,
    };
  }

  return {
    valid: true,
    fields: {
      packagingValue: value,
      packagingUnit: match,
    },
  };
}

/** Libellé affichage ex. « 500 g » */
export function formatProductPackaging(
  packagingValue?: number,
  packagingUnit?: string
): string | null {
  if (packagingValue === undefined || packagingValue === null || !packagingUnit) return null;
  if (!Number.isFinite(packagingValue) || packagingValue <= 0) return null;
  return `${packagingValue} ${packagingUnit}`;
}

export type ProductDisplayNameInput = {
  name?: string | null;
  packagingValue?: number | null;
  packagingUnit?: string | null;
};

/** Nom produit avec conditionnement en préfixe ex. « 500 g Shampoing » */
export function formatProductDisplayName(
  name: string,
  packagingValue?: number | null,
  packagingUnit?: string | null
): string {
  const packaging = formatProductPackaging(
    packagingValue ?? undefined,
    packagingUnit ?? undefined
  );
  if (!packaging) return name;
  return `${packaging} ${name}`;
}

export function getProductDisplayName(product: ProductDisplayNameInput): string {
  return formatProductDisplayName(
    String(product.name ?? '').trim() || 'Produit',
    product.packagingValue,
    product.packagingUnit
  );
}
